import React, { useState, useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Input, NumberInput, CheckInput } from "../../../../../common/Input";
import { Select } from "../../../../../common/Select";
import { TAX_TREATMENT_ID } from "../../../../../utils/constants";

const LeaveTypeAddEditModal = ({ isOpen, onClose, selectedItem, onSave, taxTreatmentsEnum, isAdminMode, data }) => {
  const [form, setForm] = useState({
    description: "",
    isEncashable: true,
    maxEncashableDays: "",
    taxExemptLimit: "",
    taxTreatment: TAX_TREATMENT_ID.NONE,
    requiredExhaustionLeaveTypeId: null,
    isActive: true,
  });

  useEffect(() => {
    if (selectedItem) {
      setForm({
        ...selectedItem,
        maxEncashableDays: selectedItem.maxEncashableDays === 0 ? "" : selectedItem.maxEncashableDays,
        taxExemptLimit: selectedItem.taxExemptLimit === 0 ? "" : selectedItem.taxExemptLimit,
        taxTreatment: selectedItem.taxTreatment ?? TAX_TREATMENT_ID.NONE,
        requiredExhaustionLeaveTypeId: selectedItem.requiredExhaustionLeaveTypeId ?? null,
      });
    } else {
      setForm({
        description: "",
        isEncashable: false,
        maxEncashableDays: "",
        taxExemptLimit: "",
        taxTreatment: TAX_TREATMENT_ID.NONE,
        requiredExhaustionLeaveTypeId: null,
        isActive: false,
      });
    }
  }, [selectedItem, isOpen]);

  const handleEncashableChange = (val) => {
    setForm(prev => ({
      ...prev,
      isEncashable: val,
      maxEncashableDays: val ? prev.maxEncashableDays : "",
      taxExemptLimit: val ? prev.taxExemptLimit : "",
    }));
  };

  // MODIFIED: Specialized handlers to treat fields differently
  const handleInputChange = (field, value, isDecimal = false) => {
    if (value === "") {
      setForm(prev => ({ ...prev, [field]: "" }));
      return;
    }

    // MODIFIED: Use parseInt for Whole numbers, parseFloat for Decimals
    const numValue = isDecimal ? parseFloat(value) : parseInt(value, 10);
    setForm(prev => ({ ...prev, [field]: isNaN(numValue) ? "" : numValue }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-3 overflow-auto"
      style={{ marginTop: "0px" }}>
      <div className="bg-white w-full md:w-[500px] rounded-lg shadow-xl flex flex-col">
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold">{selectedItem ? "Edit" : "Add"} Leave Type</h2>
          <button onClick={onClose} className="bg-transparent border-none cursor-pointer"><CloseIcon /></button>
        </div>

        <div className="p-6 space-y-4">
          <Input
            label="Description"
            placeholder="Enter leave type description"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            isRequired
          />

          <Select
            label="Prerequisite Leave (Sequential Exhaustion)"
            value={form.requiredExhaustionLeaveTypeId || ""}
            onChange={(e) => {
              const val = e.target.value;
              setForm(prev => ({
                ...prev,
                requiredExhaustionLeaveTypeId: val === "" ? null : Number(val)
              }));
            }}
            // 'data' comes from useLeaveType() via props
            options={data
              ?.filter(item => item.id !== selectedItem?.id) // Prevent a leave from requiring itself
              .map(item => ({
                value: item.id,
                label: item.description
              })) || []}
            placeholder="None - Standalone Leave"
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Tax Treatment"
              value={form.taxTreatment}
              // Use parseInt to ensure the value is stored as a number for the Enum
              onChange={(e) => {
                const newVal = parseInt(e.target.value);
                setForm(prev => ({
                  ...prev,
                  taxTreatment: newVal,

                  // this will automatically deactivate the leave type if tax treatment is set to NONE,
                  // but won't reactivate if changed back to a valid tax treatment. 
                  // Admins can manually reactivate if needed.
                  isActive: newVal === TAX_TREATMENT_ID.NONE ? false : prev.isActive
                }));
              }}
              options={taxTreatmentsEnum}
              isDisabled={!isAdminMode} // Only admins can change tax treatment

            />
            <div className="pt-6">
              <CheckInput
                name="Encashable"
                value={form.isEncashable}
                onChange={handleEncashableChange}
                isEditMode={isAdminMode} // Only admins can change tax treatment
              />
            </div>
          </div>

          <div className={`grid grid-cols-2 gap-4 transition-all ${!form.isEncashable ? 'opacity-30 pointer-events-none' : ''}`}>
            {/* Field 1: Shield - FORCED WHOLE NUMBER (Integer) */}
            <NumberInput
              label="Shield (Non-Tax Days)"
              value={form.taxExemptLimit}
              onChange={(e) => handleInputChange('taxExemptLimit', e.target.value, false)}
              placeholder="0"
              step="1" // Only whole numbers
              isDisabled={!isAdminMode} // Only admins can change tax treatment
            />

            {/* Field 2: Max Cash-out - SUPPORTS DECIMAL (0.5) */}
            <NumberInput
              label="Max Cash-out (Days)"
              value={form.maxEncashableDays}
              onChange={(e) => handleInputChange('maxEncashableDays', e.target.value, true)}
              placeholder="0"
              step="0.5" // Allows half-days
              isDisabled={!isAdminMode} // Only admins can change tax treatment
            />
          </div>

          <div className="space-y-1">
            <CheckInput
              name="Active"
              value={form.isActive}
              onChange={(val) => setForm({ ...form, isActive: val })}
              isEditMode={(form.taxTreatment !== TAX_TREATMENT_ID.NONE && selectedItem?.id) || isAdminMode} // Can only toggle active if tax treatment is valid and it's an existing item or admin mode
            />
            {form.taxTreatment === TAX_TREATMENT_ID.NONE && (
              <p className="text-[10px] text-red-500 italic mt-1 leading-tight">
                * A specific Tax Treatment must be selected before this leave type can be activated. Please inform your provider to update the tax treatment settings.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end space-x-2 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded border-none cursor-pointer">Cancel</button>
          <button onClick={() => onSave(form)} className="px-6 py-2 bg-blue-600 text-white rounded border-none cursor-pointer">Save</button>
        </div>
      </div>
    </div>
  );
};

export default LeaveTypeAddEditModal;